import type { WorldEventHandler, WorldEventContext } from '../types';
import { EventQuestAlms } from '../types';
import type {
  WorldEventStartPayload,
  WorldEventRewardPayload,
  WorldEventEndPayload,
  WanderingMerchantConfig,
  WanderingMerchantOffer,
  LootDrop,
  Rarity,
} from '../../game/types';
import { Rarity as RarityEnum } from '../../game/types';
import { registerWorldEventHandler } from '../registry';
import { npcLabel, dialogueText } from '../../i18n/contentLabels';

function parseOffers(config: unknown): WanderingMerchantOffer[] {
  const c = (config ?? {}) as WanderingMerchantConfig;
  if (!Array.isArray(c.items)) return [];
  return c.items
    .filter((row) => Number.isFinite(row?.slotIndex) && Number.isFinite(row?.cost))
    .map((row) => ({
      slotIndex: Number(row.slotIndex),
      cost: Number(row.cost),
      sold: Boolean(row.sold),
    }));
}

function onStart(payload: WorldEventStartPayload, ctx: WorldEventContext): void {
  const { locale } = ctx.locale();
  const npc = payload.npc;
  if (!npc) return;
  const phase = payload.phase === 'shop' ? 'shop' : 'approach';
  const name = npc.nameKey ? npcLabel(locale, npc.nameKey, npc.name) : npc.name;
  const message = npc.dialogueKey
    ? dialogueText(locale, npc.dialogueKey, `${name} approaches!`)
    : `${name} approaches!`;

  const offers = parseOffers(payload.config).map((o) => ({
    slotIndex: o.slotIndex,
    cost: o.cost,
    sold: o.sold ?? false,
  }));

  const engine = ctx.engine();
  if (engine) {
    if (phase === 'approach') {
      engine.setWorldEventNPCOverlay(npc, true, payload.sessionId);
    } else {
      engine.setWorldEventNPCOverlay(npc, false, payload.sessionId);
    }
  }

  const prev = ctx.getActive();
  const soldMap = new Map<number, boolean>();
  if (prev?.kind === 'questAlms') {
    for (const row of prev.offers) {
      if (row.sold) soldMap.set(row.slotIndex, true);
    }
  }

  ctx.setActive({
    kind: 'questAlms',
    eventId: EventQuestAlms,
    sessionId: payload.sessionId,
    npcName: name,
    message,
    phase,
    offers: offers.map((o) => ({
      ...o,
      sold: o.sold || soldMap.get(o.slotIndex) === true,
    })),
  });
}

function buildRewardLoot(p: WorldEventRewardPayload): LootDrop | null {
  const first = p.items?.[0];
  const rarity = (first?.rarity?.toLowerCase() ?? 'common') as Rarity;
  if (p.goldAmount != null && p.goldAmount > 0) {
    return {
      itemType: 'gold',
      rarity,
      goldAmount: p.goldAmount,
      itemName: first?.itemName ? `Sold: ${first.itemName}` : undefined,
    };
  }
  if (first?.itemName) {
    return {
      itemType: 'gold',
      rarity,
      goldAmount: 0,
      itemName: first.itemName,
    };
  }
  return null;
}

function onReward(payload: WorldEventRewardPayload, ctx: WorldEventContext): void {
  const loot = buildRewardLoot(payload);
  const engine = ctx.engine();
  if (loot && engine) engine.applyLoot(loot);
  ctx.triggerHaptic('success');
  ctx.refreshEquipment();

  const active = ctx.getActive();
  if (active?.kind === 'questAlms') {
    const spent = payload.goldSpent ?? 0;
    let slotToMark = payload.slotIndex ?? -1;
    if (slotToMark < 0 && spent > 0) {
      const match = active.offers.find((o) => !o.sold && o.cost === spent);
      if (match) slotToMark = match.slotIndex;
    }
    if (slotToMark >= 0) {
      ctx.setActive({
        ...active,
        offers: active.offers.map((o) =>
          o.slotIndex === slotToMark ? { ...o, sold: true } : o,
        ),
      });
    }
  }
  void RarityEnum;
}

function onEnd(payload: WorldEventEndPayload, ctx: WorldEventContext): void {
  const active = ctx.getActive();
  if (active?.eventId === EventQuestAlms && active.sessionId === payload.sessionId) {
    ctx.setActive(null);
  }
  const engine = ctx.engine();
  if (engine) engine.clearWorldEventNPCOverlay();
}

const handler: WorldEventHandler = {
  eventId: EventQuestAlms,
  onStart,
  onLine: () => {
    /* quest alms has no chat lines */
  },
  onReward,
  onEnd,
};

registerWorldEventHandler(handler);

export default handler;
